import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiImageSize } from '@fi-shared/types';
import { getProfileIconColor, getUserProfileAbbr } from '@fi-shared/profile-icon/profile-icon-colors';
import { ReplaySubject, Subject } from 'rxjs';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { takeUntil } from 'rxjs/operators';
export interface IUserProfile {
    Id: number; // User Id
    FullName?: string;
    MediaBankPhysicalFileName: string;
    MediaBankExtension: string;
    IsDeleted?: boolean;
}

export enum FiProfileSizes {
    Size14px = 0, // 14x14px, width 14px;
    Size18px = 1, // 18x18px, width 18px
    Size20px = 2, // 20x20px, width 20px
    Size24px = 3, // 18x18px, width 24px
    Size28px = 4,
    Size32px = 5, // 32x32 px
    Size40px = 6, // 40x40 px
    Size50px = 7, // 50x50 px
    Size150px = 8, // 150x150 px
    Size130px = 9, // 130x130 px
    Size64px = 10, // 64x64 px
}

@Component({
    selector: 'fi-profile-icon',
    template: `
        @if (getProfileImage()) {
            <img [attr.src]="getProfileImage()" class="fi-profile-icon-img" />
        } @else {
            @if (this.color) {
                <div class="fi-profile-colored" [style.background-color]="color">
                    {{ abbreviation }}
                </div>
            } @else {
                <ng-container *ngTemplateOutlet="profileIconTemplate" />
            }
        }

        @if (isUserOnline$ | async) {
            <div class="fi-online-badge"></div>
        }

        <ng-content></ng-content>

        <ng-template #profileIconTemplate>
            @if (isRegularMatIcon) {
                <mat-icon class="fi-profile-icon-icon" [ngClass]="getIconSizeClass()" />
            } @else {
                <mat-icon>
                    <span class="fi-profile-icon-icon" [ngClass]="getIconSizeClass()"></span>
                </mat-icon>
            }
        </ng-template>
    `,
    styleUrls: ['./profile-icon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-profile-icon' },
    standalone: false,
})
export class FiProfileIconComponent implements OnInit, OnDestroy {
    @Input() size = FiProfileSizes.Size32px;
    @Input() isRegularMatIcon: boolean = false;
    @Input() profilePictureIcon: string;
    @Input() solidStyle: boolean = true;
    @HostBinding('class.rounded')
    @Input()
    isRound: boolean = true;
    @Input() hideConnectedInfo: boolean = false;

    @Input() profileData: IUserProfile;
    @Input() imageSize: FiImageSize = FiImageSize.Thumbnail;

    get abbreviation(): string {
        // https://chainformation.atlassian.net/browse/FI-4043
        // set random color and name abbreviation for users without profile pictures
        if (this.profileData?.FullName) {
            return getUserProfileAbbr(this.profileData.FullName);
        }
    }
    get color(): string {
        if (this.profileData?.FullName && !this?.profileData.IsDeleted) {
            return getProfileIconColor(this.profileData.FullName);
        }
    }

    isUserOnline$ = new ReplaySubject<boolean>(1);

    constructor(
        private fiMediaStorageService: FiMediaStorageService,
        private fiSignalHelperRService: FiSignalRHelperService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        if (this.profileData) {
            // https://chainformation.atlassian.net/browse/FI-4267
            // we should properly update isUserConnected status
            const updateIsUserOnlineFn = () => {
                const isUserConnected = this.fiSignalHelperRService.isUserOnline(this.profileData.Id);
                this.isUserOnline$.next(isUserConnected);
            };
            if (!this.hideConnectedInfo) {
                updateIsUserOnlineFn();
                this.fiSignalHelperRService.onlineUserIds$
                    .pipe(takeUntil(this.ngOnDestroy$))
                    .subscribe(() => updateIsUserOnlineFn());
            }
        }
    }

    getProfileImage() {
        if (this.size === FiProfileSizes.Size150px || this.size === FiProfileSizes.Size130px) {
            this.imageSize = FiImageSize.Medium;
        }
        if (this.profileData && this.profileData.MediaBankPhysicalFileName) {
            return this.fiMediaStorageService.getMediaFileUrl(
                {
                    PhysicalFileName: this.profileData.MediaBankPhysicalFileName,
                    Extension: this.profileData.MediaBankExtension,
                },
                { size: this.imageSize },
            );
        }
    }

    @HostBinding('class')
    get class() {
        return `size-${FiProfileSizes[this.size]}`;
    }

    getIconSizeClass() {
        const classList = this.profilePictureIcon
            ? [this.profilePictureIcon]
            : ['fa-user-circle', this.solidStyle ? 'fas' : 'far'];
        if (this.size === FiProfileSizes.Size18px) classList.push('fa-lg');
        if (this.size === FiProfileSizes.Size32px) classList.push('fa-2x');
        if (this.size === FiProfileSizes.Size40px) classList.push('fa-3x');
        if (this.size === FiProfileSizes.Size50px) classList.push('fa-4x');
        return classList.join(' ');
    }

    detectChanges() {
        this.cdr.markForCheck();
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
